import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Calendar,
  MapPin,
  Clock,
  ExternalLink,
  Trophy,
  Users,
  Target,
  LogOut,
  User,
  Crown,
  ChevronRight,
  CalendarDays
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import { EventsSubNav } from '@/components/EventsSubNav';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const SchedulePage = () => {
  const navigate = useNavigate();
  const { member, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API_URL}/schedule`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatEventTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isUpcoming = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) >= new Date();
  };

  const isPast = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const upcomingEvents = events.filter(e => isUpcoming(e.event_date));
  const pastEvents = events.filter(e => isPast(e.event_date));

  const getAddressLink = (event) => {
    if (event.venue_maps_link) return event.venue_maps_link;
    if (event.venue_address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue_address)}`;
    }
    return null;
  };

  const renderEventCard = (event, index) => {
    const upcoming = isUpcoming(event.event_date);
    const addressLink = getAddressLink(event);

    return (
      <Card
        key={event.id}
        className={`bg-card border-white/10 animate-fade-in ${upcoming ? 'tournament-card' : 'opacity-70'}`}
        style={{ animationDelay: `${index * 0.05}s` }}
        data-testid={`schedule-event-${index}`}
      >
        <CardContent className="p-0">
          <div className="flex flex-col md:flex-row">
            {/* Date column */}
            <div className={`flex-shrink-0 p-6 flex flex-col items-center justify-center md:w-36 border-b md:border-b-0 md:border-r border-white/10 ${upcoming ? 'bg-primary/10' : 'bg-white/5'}`}>
              <span className={`text-3xl font-mono font-bold ${upcoming ? 'text-primary' : 'text-muted-foreground'}`}>
                {event.event_date ? new Date(event.event_date).getDate() : '--'}
              </span>
              <span className={`text-sm uppercase tracking-wider ${upcoming ? 'text-primary/80' : 'text-muted-foreground'}`}>
                {event.event_date ? new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' }) : ''}
              </span>
              <span className="text-xs text-muted-foreground mt-1">
                {event.event_date ? new Date(event.event_date).getFullYear() : ''}
              </span>
            </div>

            {/* Content */}
            <div className="flex-1 p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  {/* Tournament name & badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <h3
                      className="text-lg font-heading text-white cursor-pointer hover:text-primary transition-colors"
                      onClick={() => navigate(`/tournaments/${event.id}`)}
                      data-testid={`event-name-${index}`}
                    >
                      {event.name}
                    </h3>
                    {event.tournament_type === 'pro' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/50">
                        <Crown className="w-3 h-3" />
                        Pro Tour
                      </span>
                    )}
                    {!upcoming && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-muted-foreground border border-white/10">
                        Past
                      </span>
                    )}
                  </div>

                  {/* Event details */}
                  <div className="space-y-2">
                    {/* Time */}
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-white">
                        {formatEventDate(event.event_date)} at {formatEventTime(event.event_date)}
                      </span>
                    </div>

                    {/* Venue */}
                    {event.venue_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <Trophy className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-white">{event.venue_name}</span>
                      </div>
                    )}

                    {/* Address */}
                    {(event.venue_address || event.venue_maps_link) && (
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        {addressLink ? (
                          <a
                            href={addressLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 underline underline-offset-2 flex items-center gap-1"
                            data-testid={`event-address-link-${index}`}
                          >
                            {event.venue_address || 'View on Google Maps'}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">{event.venue_address}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tournament info */}
                  <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {event.num_holes} Holes
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {event.participants_count} Players
                    </span>
                    <span className="text-primary font-mono">
                      Par {event.total_par}
                    </span>
                  </div>
                </div>

                {/* Action */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/tournaments/${event.id}`)}
                  className="text-muted-foreground hover:text-white self-start"
                  data-testid={`event-view-${index}`}
                >
                  View
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img
              src={TGA_LOGO}
              alt="TGA Pro Tour"
              className="h-10 object-contain cursor-pointer"
              onClick={() => navigate('/dashboard')}
              data-testid="nav-logo"
            />
            <div className="hidden sm:flex items-center gap-1">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                className="text-white text-sm border-b-2 border-primary rounded-none"
                data-testid="nav-schedule-active"
              >
                Schedule
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/tours')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-tours"
              >
                Tours
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/rankings')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-rankings"
              >
                Rankings
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {member?.is_admin && (
              <span className="px-2 py-1 rounded bg-primary/20 text-primary text-xs font-medium border border-primary/50">
                Admin
              </span>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="text-muted-foreground hover:text-white"
              data-testid="nav-profile-btn"
            >
              <User className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{member?.name}</span>
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-destructive"
              data-testid="nav-logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <EventsSubNav />
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <CalendarDays className="w-8 h-8 text-primary" />
            <h1 className="text-3xl sm:text-4xl font-heading text-white">
              Pro Tour Schedule
            </h1>
          </div>
          <p className="text-muted-foreground">
            Upcoming and past tournament events with venues and times
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-primary">Loading schedule...</div>
          </div>
        ) : events.length === 0 ? (
          <Card className="bg-card border-white/10">
            <CardContent className="p-12 text-center">
              <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading text-white mb-2">No Events Scheduled</h3>
              <p className="text-muted-foreground mb-6">
                No tournaments have been scheduled with event dates yet.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-white/20 hover:bg-white/5 rounded-pill"
                data-testid="back-to-dashboard"
              >
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Upcoming Events */}
            {upcomingEvents.length > 0 && (
              <div>
                <h2 className="text-lg font-heading text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Upcoming Events
                </h2>
                <div className="grid gap-4">
                  {upcomingEvents.map((event, i) => renderEventCard(event, i))}
                </div>
              </div>
            )}

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <div>
                <h2 className="text-lg font-heading text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground"></span>
                  Past Events
                </h2>
                <div className="grid gap-4">
                  {pastEvents.map((event, i) => renderEventCard(event, upcomingEvents.length + i))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};
